import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash, CaretDown, CaretRight, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth, canManage } from "@/contexts/AuthContext";

const TIPO_LABEL = {
  oficial: "Oficial (LFT)",
  empresa: "Cierre empresa",
  puente: "Puente",
  vacaciones: "Vacaciones",
};
const TIPO_BADGE = {
  oficial: "bg-indigo-100 text-indigo-800",
  empresa: "bg-amber-100 text-amber-900",
  puente: "bg-sky-100 text-sky-800",
  vacaciones: "bg-emerald-100 text-emerald-800",
};

const fmtFecha = (s) =>
  s ? new Date(`${s}T00:00:00`).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }) : "—";

export default function CalendarioLaboral() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const puedeGestionar = canManage(user);
  const currentYear = new Date().getFullYear();

  const [anio, setAnio] = useState(String(currentYear));
  const [items, setItems] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);      // día individual en edición
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({});      // grupo_id -> bool

  const load = async () => {
    const reqs = [api.get(`/dias-inhabiles?anio=${anio}`)];
    if (isSuper) reqs.push(api.get("/empresas"));
    const [d, e] = await Promise.all(reqs);
    setItems(d.data);
    if (e) setEmpresas(e.data);
  };
  useEffect(() => { load(); }, [anio]); // eslint-disable-line react-hooks/exhaustive-deps

  const empresaName = (id) =>
    id == null ? "Global (todas)" : empresas.find((x) => x.id === id)?.razon_social || "Mi empresa";

  // Agrupa por grupo_id (periodos) y deja sueltos los días individuales.
  const rows = useMemo(() => {
    const groups = {};
    const singles = [];
    for (const d of items) {
      if (d.grupo_id) (groups[d.grupo_id] ||= []).push(d);
      else singles.push({ ...d, _period: false });
    }
    const periods = Object.entries(groups).map(([gid, days]) => {
      const s = [...days].sort((a, b) => a.fecha.localeCompare(b.fecha));
      return {
        _period: true, grupo_id: gid, dias: s,
        fecha: s[0].fecha, fecha_fin: s[s.length - 1].fecha,
        nombre: s[0].nombre, tipo: s[0].tipo, empresa_id: s[0].empresa_id, count: s.length,
      };
    });
    return [...singles, ...periods].sort((a, b) => a.fecha.localeCompare(b.fecha));
  }, [items]);

  const puedeEditarFila = (row) =>
    puedeGestionar && (row.empresa_id != null || isSuper);

  const startCreate = () => {
    setEditing(null);
    setForm({
      nombre: "", tipo: "empresa",
      ambito: isSuper ? "global" : "empresa",   // no-super: siempre su empresa
      fecha: `${anio}-01-01`, fecha_fin: "", es_rango: false,
    });
    setOpen(true);
  };

  const startEditDia = (dia) => {
    setEditing(dia);
    setForm({ nombre: dia.nombre, tipo: dia.tipo });
    setOpen(true);
  };

  const submit = async () => {
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/dias-inhabiles/${editing.id}`, { nombre: form.nombre, tipo: form.tipo });
        toast.success("Día actualizado");
      } else {
        const empresa_id =
          form.ambito === "global" ? null
          : form.ambito === "empresa" ? user.empresa_id
          : form.ambito;
        const payload = { empresa_id, fecha: form.fecha, nombre: form.nombre, tipo: form.tipo };
        if (form.es_rango && form.fecha_fin) payload.fecha_fin = form.fecha_fin;
        const r = await api.post("/dias-inhabiles", payload);
        toast.success(r.data.creados > 1 ? `Periodo creado (${r.data.creados} días)` : "Día inhábil creado");
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const borrarDia = async (id) => {
    if (!window.confirm("¿Eliminar este día del calendario?")) return;
    try {
      await api.delete(`/dias-inhabiles/${id}`);
      toast.success("Día eliminado");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const borrarPeriodo = async (gid) => {
    if (!window.confirm("¿Eliminar todo el periodo del calendario?")) return;
    try {
      const r = await api.delete(`/dias-inhabiles/grupo/${gid}`);
      toast.success(`Periodo eliminado (${r.data.eliminados} días)`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const tipoOptions = isSuper
    ? ["oficial", "empresa", "puente", "vacaciones"]
    : ["empresa", "puente", "vacaciones"];

  return (
    <div className="p-6 md:p-10" data-testid="calendario-page">
      <PageHeader
        kicker="Catálogos"
        title="Calendario Laboral"
        subtitle="Días de descanso obligatorio (Art. 74 LFT), cierres de la empresa, puentes y periodos vacacionales. Un día del calendario no genera falta ni retardo."
        actions={
          <div className="flex gap-2 items-center">
            <Select value={anio} onValueChange={setAnio}>
              <SelectTrigger className="w-28" data-testid="cal-anio"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {puedeGestionar && (
              <Button onClick={startCreate} className="bg-brand hover:bg-brand-hover" data-testid="cal-new-btn">
                <Plus size={16} weight="bold" className="mr-2" /> Nuevo día / periodo
              </Button>
            )}
          </div>
        }
      />

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Nombre</th>
                <th>Tipo</th>
                <th>Ámbito</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={5} className="text-center text-slate-500 py-8">Sin días registrados para {anio}</td></tr>
              )}
              {rows.map((row) => {
                if (!row._period) {
                  return (
                    <tr key={row.id} data-testid={`cal-row-${row.id}`}>
                      <td className="whitespace-nowrap">{fmtFecha(row.fecha)}</td>
                      <td className="font-medium">{row.nombre}</td>
                      <td><span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${TIPO_BADGE[row.tipo] || "bg-slate-100"}`}>{TIPO_LABEL[row.tipo] || row.tipo}</span></td>
                      <td className="text-xs text-slate-600">{empresaName(row.empresa_id)}</td>
                      <td className="whitespace-nowrap text-right">
                        {puedeEditarFila(row) && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => startEditDia(row)}><PencilSimple size={15} /></Button>
                            <Button variant="ghost" size="sm" onClick={() => borrarDia(row.id)}><Trash size={15} /></Button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                }
                const isOpen = !!expanded[row.grupo_id];
                return (
                  <FragmentRows key={row.grupo_id}>
                    <tr className="bg-slate-50/60" data-testid={`cal-period-${row.grupo_id}`}>
                      <td className="whitespace-nowrap">
                        <button
                          className="inline-flex items-center gap-1 font-medium"
                          onClick={() => setExpanded((s) => ({ ...s, [row.grupo_id]: !isOpen }))}
                        >
                          {isOpen ? <CaretDown size={13} /> : <CaretRight size={13} />}
                          {fmtFecha(row.fecha)} – {fmtFecha(row.fecha_fin)}
                        </button>
                        <span className="ml-2 text-xs text-slate-400">{row.count} días</span>
                      </td>
                      <td className="font-medium">{row.nombre}</td>
                      <td><span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${TIPO_BADGE[row.tipo] || "bg-slate-100"}`}>{TIPO_LABEL[row.tipo] || row.tipo}</span></td>
                      <td className="text-xs text-slate-600">{empresaName(row.empresa_id)}</td>
                      <td className="text-right whitespace-nowrap">
                        {puedeEditarFila(row) && (
                          <Button variant="ghost" size="sm" onClick={() => borrarPeriodo(row.grupo_id)}>
                            <Trash size={15} className="mr-1" /> Eliminar periodo
                          </Button>
                        )}
                      </td>
                    </tr>
                    {isOpen && row.dias.map((d) => (
                      <tr key={d.id} className="text-sm text-slate-500">
                        <td className="pl-8 whitespace-nowrap">{fmtFecha(d.fecha)}</td>
                        <td colSpan={3}>{d.nombre}</td>
                        <td className="text-right">
                          {puedeEditarFila(row) && (
                            <Button variant="ghost" size="sm" onClick={() => borrarDia(d.id)}><Trash size={14} /></Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </FragmentRows>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="cal-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? "Editar día" : "Nuevo día / periodo inhábil"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1">
              <Label className="label-uppercase">Nombre</Label>
              <Input value={form.nombre || ""} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder="Ej. Cierre vacacional de invierno" data-testid="cal-nombre" />
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase">Tipo</Label>
              <Select value={form.tipo || "empresa"} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {tipoOptions.map((t) => <SelectItem key={t} value={t}>{TIPO_LABEL[t]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {!editing && isSuper && (
              <div className="space-y-1">
                <Label className="label-uppercase">Ámbito</Label>
                <Select value={form.ambito} onValueChange={(v) => setForm({ ...form, ambito: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="global">Global (todas las empresas)</SelectItem>
                    {empresas.map((e) => <SelectItem key={e.id} value={e.id}>{e.razon_social}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {!editing && (
              <>
                <label className="col-span-2 flex items-center gap-2 text-sm mt-1">
                  <input type="checkbox" checked={!!form.es_rango}
                    onChange={(e) => setForm({ ...form, es_rango: e.target.checked })} />
                  Es un periodo (rango de fechas)
                </label>
                <div className="space-y-1">
                  <Label className="label-uppercase">{form.es_rango ? "Desde" : "Fecha"}</Label>
                  <Input type="date" value={form.fecha || ""} onChange={(e) => setForm({ ...form, fecha: e.target.value })} data-testid="cal-fecha" />
                </div>
                {form.es_rango && (
                  <div className="space-y-1">
                    <Label className="label-uppercase">Hasta</Label>
                    <Input type="date" value={form.fecha_fin || ""} onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })} />
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={busy} className="bg-brand hover:bg-brand-hover" data-testid="cal-save">
              {busy ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Helper para devolver varias <tr> sin envoltorio que rompa la tabla.
function FragmentRows({ children }) {
  return <>{children}</>;
}
